import { useEffect, useRef, useState } from 'react';

const faqGroups = [
  {
    title: '1. 判断のプロセスについて',
    faqs: [
      {
        q: 'Q:なぜ相談が「初回」と「後日」の2回に分かれているのですか?',
        a: 'A:精密診断には、一定の分析時間が必要だからです。\n当院では、「その場で決めさせないこと」も医療の一部だと考えています。',
      },
      {
        q: 'Q:他の医院で「マウスピース矯正ができる」と言われましたが、こちらで「向かない」と言われることもありますか?',
        a: 'A:あります。\n当院では、見た目の変化だけでなく、歯根(根っこ)の安全性や、10年後の安定性を重視しています。\nそのため医学的な観点から、あえて別の方法をおすすめする場合があります。',
      },
    ],
  },
  {
    title: '2. 費用と構造について',
    faqs: [
      {
        q: 'Q:なぜWebサイトに具体的な「総額」が載っていないのですか?',
        a: 'A:歯並びの状態や、目指すゴールの精度によって、必要なマウスピースの枚数や処置内容が大きく異なるためです。\nあなた専用《矯正プラン比較レポート》では、「費用」「追加費用が発生する条件」まで含めて、すべてご説明します。',
      },
    ],
  },
  {
    title: '3. 失敗と責任について',
    faqs: [
      {
        q: 'Q:シミュレーション通りに動かなかった場合はどうなりますか?',
        a: 'A:生体反応には個人差があるため、計画の修正が必要になる場合があります。\nその際の対応方針や、どこまでが想定内で、どこから再設計になるのかも、事前にレポートでご説明しています。',
      },
      {
        q: 'Q:このレポートを持って、他の医院に相談に行ってもいいですか?',
        a: 'A:もちろんです。\nむしろ、ご自身が納得するための「判断軸」として活用していただくことを推奨しています。',
      },
    ],
  },
];

export default function PhilosophyChapter06() {
  const sectionRef = useRef<HTMLElement>(null);
  const [openMap, setOpenMap] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.fade-up, .fade-up-item, .scale-in').forEach((el) => el.classList.add('visible'));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const toggle = (key: string) => {
    setOpenMap((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <section ref={sectionRef} id="chapter8" className="py-20 px-6 bg-gray-50">
      <div className="max-w-4xl mx-auto">
        <div className="mb-12 fade-up">
          <p className="text-lg font-bold mb-4" style={{ color: '#3E5F8F' }}>06</p>
          <h2 className="text-3xl md:text-4xl font-normal text-gray-900 mb-4" style={{ fontFamily: 'Noto Serif JP, serif' }}>
            よくある質問
          </h2>
          <p className="text-lg text-gray-600 mb-8">― 判断のために、事前に知っておいてほしいこと ―</p>
          <div className="scale-in">
            <img
              alt="よくある質問"
              className="w-full rounded-lg mb-12"
              src="https://storage.googleapis.com/studio-design-asset-files/projects/Kwa5AGrYaX/s-1456x816_v-fms_webp_f3c27215-403a-4530-9b09-0e1c3e232b3a_small.webp"
            />
          </div>
        </div>

        <div className="space-y-12">
          {faqGroups.map((group, gi) => (
            <div key={gi} className="fade-up">
              <h3 className="text-xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Noto Serif JP, serif' }}>{group.title}</h3>
              <div className="space-y-4">
                {group.faqs.map((faq, fi) => {
                  const key = `${gi}-${fi}`;
                  return (
                    <div key={fi} className="bg-white rounded-lg overflow-hidden">
                      <button
                        className="w-full px-6 py-5 flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer text-left"
                        onClick={() => toggle(key)}
                      >
                        <p className="text-sm font-medium text-gray-900 pr-4">{faq.q}</p>
                        <i className={`ri-add-line text-2xl text-gray-400 transition-transform duration-300 flex-shrink-0 ${openMap[key] ? 'rotate-45' : ''}`} />
                      </button>
                      <div
                        className="overflow-hidden"
                        style={{
                          maxHeight: openMap[key] ? '400px' : '0',
                          opacity: openMap[key] ? 1 : 0,
                          transition: 'max-height 0.4s ease, opacity 0.3s ease',
                        }}
                      >
                        <div className="px-6 pb-6">
                          <p className="text-sm text-gray-700 whitespace-pre-line">{faq.a}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 rounded-lg p-8 fade-up" style={{ backgroundColor: '#EEF3F9' }}>
          <h4 className="text-lg font-bold text-gray-900 mb-4" style={{ fontFamily: 'Noto Serif JP, serif' }}>まとめ</h4>
          <p className="text-sm text-gray-700 leading-relaxed">
            よくある不安を解消しておくことは、<br />
            単なる安心のためではなく、あなたの「判断軸」を確かなものにするために必要です。<br />
            当院では、医学的根拠に基づいた情報提供に徹し、その場で結論を急がせないことをお約束しています。
          </p>
        </div>
      </div>
    </section>
  );
}